import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Input, List, Spin, Menu, Form } from "antd";
import Icon from "@ant-design/icons";
import { NamePath } from "antd/es/form/interface";
import { getGoogleMapsLoader } from "../../utils/googleMapsLoader";
import { StyledInput } from "../Input/styles";

const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const LocationSvg = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 384 512"
    width="1em"
    height="1em"
    fill="currentColor"
  >
    <path d="M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z" />
  </svg>
);

const LocationIcon = (props: React.ComponentProps<typeof Icon>) => <Icon component={LocationSvg} {...props} />;

export interface Place {
  address1: string;
  city: string;
  state: string;
  stateCode: string;
  country: string;
  countryCode: string;
  postalCode: string;
  placeId: string;
  name?: string;
  displayName?: string;
  formattedAddress?: string;
}

// interface Prediction {
//   placePrediction: {
//     toPlace: () => { place_id: string };
//     description: string;
//     distance_meters?: number;
//     matched_substrings: google.maps.places.PredictionSubstring[];
//     place_id: string;
//     structured_formatting: {
//       main_text: string;
//       secondary_text: string;
//     };
//     terms: google.maps.places.PredictionTerm[];
//     types: string[];
//   };
// }

interface GoogleAutocompleteInputProps {
  onPlaceSelected?: (place: Place | null) => void;
  inputProps?: any;
  googleMapsOptions?: any;
  value?: string;
  onChange?: (value: string) => void;
  placeId?: string;
  placeholder?: string;
  label?: string;
  name?: NamePath<any>;
  form?: any;
  country?: string;
  fontWeight?: string;
  rules?: any[];
  onBlur?: any;
  hidden?: boolean;
  disabled?: boolean;
}

function parseAddressComponents(addressComponents: google.maps.GeocoderAddressComponent[], formate_address: string | undefined): Place {
  const address: Place = {
    address1: "",
    city: "",
    state: "",
    stateCode: "",
    country: "",
    countryCode: "",
    postalCode: "",
    placeId: "",
    name: "",
    displayName: "",
    formattedAddress: ""
  };

  let streetNumber = "";
  let route = "";
  let sublocality = "";
  let premise = "";
  let subpremise = "";
  let address1 = "";

  addressComponents.forEach((component: any) => {
    const types = component.types;
    const valueLong = component.longText ?? "";
    const valueShort = component.shortText ?? "";

    if (types.includes("street_number")) {
      streetNumber = valueLong;
    } else if (types.includes("route")) {
      route = valueLong;
    } else if (types.includes("sublocality_level_1") || types.includes("sublocality")) {
      sublocality = valueLong;
    } else if (types.includes("premise")) {
      premise = valueLong;
    } else if (types.includes("subpremise")) {
      subpremise = valueLong;
    } else if (types.includes("locality")) {
      address.city = valueLong;
      address1 = formate_address?.split(`, ${valueLong}`)[0] || "";
    } else if (types.includes("administrative_area_level_1")) {
      address.state = valueLong;
      address.stateCode = valueShort;
    } else if (types.includes("country")) {
      address.country = valueLong;
      address.countryCode = valueShort;
    } else if (types.includes("postal_code")) {
      address.postalCode = valueLong;
    }
  });

  if (address1.length > 0) {
    address.address1 = address1;
  } else {
    const addressParts = [
      streetNumber,
      route,
      sublocality,
      premise,
      subpremise
    ].filter(Boolean);

    address.address1 = addressParts.reverse().join(", ").trim();
  }

  if (!address?.state?.length) {
    address.state = address.country
  }
  if (!address?.stateCode?.length) {
    address.stateCode = address.countryCode
  }

  if (!address?.city?.length) {
    address.city = address?.state;
  }

  if (!address.address1 && premise) {
    address.address1 = premise;
  }

  return address;
}

const GoogleAutocompleteInput = ({
  onPlaceSelected,
  inputProps,
  googleMapsOptions,
  value,
  onChange,
  placeId,
  placeholder,
  label,
  name,
  form,
  // country,
  fontWeight,
  rules,
  onBlur,
  hidden,
  disabled,
}: GoogleAutocompleteInputProps) => {
  // const [inputValue, setInputValue] = useState(value || "");
  const [predictions, setPredictions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMapsApiLoaded, setIsMapsApiLoaded] = useState(false);
  const [mapsApiLoadError, setMapsApiLoadError] = useState<Error | null>(null);
  // const currentRegionRef = useRef<string | undefined>(country || googleMapsOptions?.region);

  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const autocompleteInputRef = useRef<{ input: HTMLInputElement }>(null);
  const suggestionsListRef = useRef<HTMLDivElement>(null);
  const isSuggestionClickRef = useRef(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const isClearedByAntdRef = useRef(false);

  useEffect(() => {
    if (!apiKey) {
      setMapsApiLoadError(new Error("API key is missing."));
      return;
    }

    const loader = getGoogleMapsLoader();

    loader.importLibrary("places")
      .then(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          setIsMapsApiLoaded(true);
          // if (country !== currentRegionRef.current) {
          //   currentRegionRef.current = country;
          // }
        } else {
          setMapsApiLoadError(new Error("Places library not available after API load."));
        }
      })
      .catch((error: Error) => {
        setMapsApiLoadError(error);
      });
  }, []);

  const createSessionToken = useCallback(() => {
    if (isMapsApiLoaded && window.google?.maps?.places) {
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    }
  }, [isMapsApiLoaded]);

  const invalidateSessionToken = useCallback(() => {
    sessionTokenRef.current = null;
  }, []);

  const fetchPredictions = useCallback(async (input: string) => {
    // Early return conditions to prevent unnecessary processing
    if (!isMapsApiLoaded || !input || input.trim().length < (googleMapsOptions?.minCharsForSuggestions || 2)) {
      setPredictions([]);
      setShowSuggestions(false);
      return;
    }

    // Ensure we have a valid session token
    if (!sessionTokenRef.current) {
      createSessionToken();
      if (!sessionTokenRef.current) {
        setError("Session token could not be created.");
        return;
      }
    }

    // Use a flag to prevent state updates if component unmounts during API call
    let isMounted = true;
    setLoading(true);
    setError(null);

    try {
      const requestOptions = {
        input: input,
        sessionToken: sessionTokenRef.current,
        ...(googleMapsOptions?.autocompleteOptions || {}),
      };

      // @ts-ignore
      const { suggestions } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
        requestOptions
      );

      // Only update state if component is still mounted
      if (!isMounted) return;

      setPredictions(suggestions || []);
      setShowSuggestions(suggestions && suggestions.length > 0);

      // Only focus if we actually have suggestions
      if (suggestions && suggestions.length > 0) {
        autocompleteInputRef.current?.input?.focus();
      }
    } catch (err: any) {
      console.error("Error fetching autocomplete suggestions:", err);

      // Only update state if component is still mounted
      if (!isMounted) return;

      setError("Failed to fetch suggestions.");
      setPredictions([]);
      setShowSuggestions(false);

      if (err.code === "INVALID_REQUEST" || err.message?.includes("Session token")) {
        invalidateSessionToken(); // Reset token if it becomes invalid
      }
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }

    // Return cleanup function to set mounted flag to false
    return () => {
      isMounted = false;
    };
  }, [isMapsApiLoaded, createSessionToken, googleMapsOptions]);

  // useEffect(() => {
  //   if (value !== undefined && value !== inputValue) {
  //     setInputValue(value);
  //   }
  // }, [value, inputValue]);

  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    onChange?.(newValue);

    // Handle empty input case
    if (!newValue.trim()) {
      isClearedByAntdRef.current = true;
      setPredictions([]);
      setShowSuggestions(false);
      setError(null);
      onPlaceSelected?.(null);
      invalidateSessionToken();
      return;
    }

    isClearedByAntdRef.current = false;
    if (!sessionTokenRef.current) createSessionToken();

    // Implement proper debouncing to prevent rapid API calls
    clearTimeout(debounceTimeoutRef.current);
    debounceTimeoutRef.current = setTimeout(() => {
      fetchPredictions(newValue);
    }, googleMapsOptions?.debounce || 300); // Increased debounce time for better performance
  }, [onChange, onPlaceSelected, invalidateSessionToken, createSessionToken, fetchPredictions, googleMapsOptions]);

  const handleSelectPlace = useCallback(async (clickedItemData: any) => {
    if (!clickedItemData) return;

    isSuggestionClickRef.current = true;

    // Early return conditions
    if (!isMapsApiLoaded) return;
    if (!sessionTokenRef.current) {
      setError("Session error. Please try searching again.");
      invalidateSessionToken();
      setShowSuggestions(false);
      setPredictions([]);
      return;
    }

    // Use a flag to prevent state updates if component unmounts during API call
    let isMounted = true;
    setLoading(true);
    setError(null);
    setShowSuggestions(false);
    setPredictions([]);

    try {
      const place = clickedItemData.toPlace();
      await place.fetchFields({
        fields: [
          "addressComponents",
          "formattedAddress",
          "id",
          "displayName",
        ],
        sessionToken: sessionTokenRef.current,
      });

      // Invalidate token after successful use
      invalidateSessionToken();

      // Only proceed if component is still mounted
      if (!isMounted) return;

      if (form && name) {
        form.setFieldValue(name, place.formattedAddress);
      }

      if (onChange) {
        onChange(place.formattedAddress);
      }

      const detailedAddress: Place = place.addressComponents ?
        parseAddressComponents(place.addressComponents, place.formattedAddress) :
        {
          address1: "",
          city: "",
          state: "",
          stateCode: "",
          country: "",
          countryCode: "",
          postalCode: "",
          placeId: "",
          name: "",
          displayName: "",
          formattedAddress: place.formattedAddress
        };

      if (onPlaceSelected) {
        onPlaceSelected({
          ...detailedAddress,
          placeId: place.id ?? "",
          name: place.displayName ?? "",
          displayName: place.displayName ?? "",
          formattedAddress: place.formattedAddress,
          address1: detailedAddress.address1 ?? "",
          city: detailedAddress.city ?? "",
          state: detailedAddress.state ?? "",
          stateCode: detailedAddress.stateCode ?? "",
          country: detailedAddress.country ?? "",
          countryCode: detailedAddress.countryCode ?? "",
          postalCode: detailedAddress.postalCode ?? "",
        });
      }
    } catch (err) {
      // Only update state if component is still mounted
      if (!isMounted) return;

      setError("Failed to get place details.");
      invalidateSessionToken();
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }

    // Return cleanup function
    return () => {
      isMounted = false;
    };
  }, [isMapsApiLoaded, onPlaceSelected, onChange, invalidateSessionToken, form, name]);

  const handleAntdInputBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    setTimeout(() => {
      const relatedTarget = e.relatedTarget;
      if (
        suggestionsListRef.current &&
        !suggestionsListRef.current.contains(relatedTarget) &&
        autocompleteInputRef.current?.input !== relatedTarget
      ) {
        if (!isSuggestionClickRef.current) setShowSuggestions(false);
      }
      isSuggestionClickRef.current = false;
    }, 150);

    if (onBlur) {
      onBlur(e);
    }
  }, [onBlur]);

  useEffect(() => {
    return () => {
      clearTimeout(debounceTimeoutRef.current);
      invalidateSessionToken();
    };
  }, [invalidateSessionToken]);

  // Only fetch place details when placeId changes and is not empty
  const prevPlaceIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    // Only create session token once when component mounts
    createSessionToken();
  }, [createSessionToken]);

  useEffect(() => {
    // Skip if placeId hasn't changed or is the same as previous
    if (!placeId || placeId === prevPlaceIdRef.current || !isMapsApiLoaded || !window.google?.maps?.places) {
      return;
    }

    prevPlaceIdRef.current = placeId;

    // Use a flag to prevent state updates if component unmounts during API call
    let isMounted = true;

    const place: any = new (window.google.maps.places as any).Place(
      { id: placeId }
    );

    place.fetchFields({
      fields: ["addressComponents", "formattedAddress", "id", "displayName"],
      sessionToken: sessionTokenRef.current,
    }).then(() => {
      if (!isMounted) return;

      const formattedAddress = place.formattedAddress || "";
      if (form && name) {
        form.setFieldValue(name, formattedAddress);
      }
      if (onChange) {
        onChange(formattedAddress);
      }
    }).catch((err: any) => {
      console.error("Error fetching place details:", err);
    });

    return () => {
      isMounted = false;
    };
  }, [placeId, isMapsApiLoaded, onChange, form, name]);

  // Memoize menu items to prevent unnecessary recalculations
  const menuItems = useMemo(() => {
    return predictions.map((suggestion) => {
      const placePred: any = suggestion.placePrediction;
      if (!placePred) return null; // Should not happen with valid predictions

      const mainText = placePred.structured_formatting?.main_text ||
        placePred.text?.toString()?.split(",")[0] || "";

      const secondaryText = placePred.structured_formatting?.secondary_text ||
        placePred.text?.toString()?.substring(mainText.length)?.replace(/^,/, "").trim() || "";

      return {
        key: placePred.placeId,
        label: (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontWeight: 500, lineHeight: "1.2" }}>
              {mainText}
            </span>
            {secondaryText && (
              <span
                style={{
                  fontSize: "0.9em",
                  color: "#777",
                  lineHeight: "1.2",
                }}
              >
                {secondaryText}
              </span>
            )}
          </div>
        ),
        icon: <LocationIcon style={{ color: "#555", fontSize: "16px" }} />,
        "data-suggestion": suggestion,
        style: {
          padding: "8px 12px",
          lineHeight: "normal",
          height: "auto",
          display: "flex",
          alignItems: "center",
          ...inputProps?.suggestionMenuItemStyle,
        },
        onClick: () => handleSelectPlace(placePred),
      }
    }).filter((item) => item !== null);
  }, [predictions, handleSelectPlace, inputProps?.suggestionMenuItemStyle]);

  // --- Handle Input Focus ---
  const handleFocus = useCallback(() => {
    // Check if Maps API is loaded and no active session
    const shouldCreateToken = isMapsApiLoaded && !sessionTokenRef.current;
    if (shouldCreateToken) {
      createSessionToken();
    }
    // Check if we should show suggestions
    const shouldShowSuggestions = form && name && form.getFieldValue(name) && predictions.length > 0;
    if (shouldShowSuggestions) {
      setShowSuggestions(true);
    }
  }, [isMapsApiLoaded, createSessionToken, form, name, predictions.length]);

  // Early returns for special cases
  if (hidden) return null;
  if (mapsApiLoadError) return <div style={{ color: "red" }}>Error: {mapsApiLoadError.message}</div>;
  if (!isMapsApiLoaded && !apiKey) return <div style={{ color: "red" }}>API Key required.</div>;
  if (!isMapsApiLoaded && apiKey) return <Spin />;

  return (
    <div style={{ position: "relative", paddingBottom: 24 }}>
      <Form.Item name={name} label={label} rules={rules} style={{ marginBottom: 0 }}>
        <StyledInput
          ref={autocompleteInputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleAntdInputBlur}
          placeholder={placeholder || "Enter an address..."}
          size="large"
          allowClear={!loading}
          suffix={loading ? <Spin size="small" /> : inputProps?.suffix}
          disabled={disabled}
          {...inputProps}
          style={{ width: "100%", ...inputProps?.style }}
        />
      </Form.Item>
      {isMapsApiLoaded && showSuggestions && predictions.length > 0 && (
        <div
          ref={suggestionsListRef}
          onMouseDownCapture={() => {
            isSuggestionClickRef.current = true;
          }}
          style={{
            position: "absolute",
            width: "100%",
            backgroundColor: "white",
            border: "1px solid #d9d9d9",
            borderRadius: "2px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
            zIndex: 1050,
            maxHeight: "300px",
            overflowY: "auto",
            ...inputProps?.suggestionsContainerStyle,
          }}
        >
          <Menu items={menuItems} />
        </div>
      )}
    </div>
  );
};

export default GoogleAutocompleteInput;
